/**
 * Formatting helpers for mobile (app) subscription rows.
 *
 * Shared by the parent Setting page and the parent Billing page's Mobile tab so both
 * render the same rows the same way.
 */

export const formatPlanPrice = (plan) => {
  if (!plan) return "-";
  const pounds = (Number(plan.price_pence || 0) / 100).toFixed(2);
  const symbol = String(plan.currency || "").toLowerCase() === "gbp" ? "£" : `${String(plan.currency || "").toUpperCase()} `;
  return `${symbol}${pounds}`;
};

const LONDON = "Europe/London";

const NAIVE_STAMP = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?$/;
const DMY_STAMP = /^(\d{1,2})-([A-Za-z]{3})-(\d{4})$/;
const MONTHS = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];

/**
 * Parse a timestamp from the API into a Date plus the timezone it should be FORMATTED in.
 *
 * The API sends three shapes and they must not be treated alike:
 *  - "2026-09-18 14:30:00" (toDateTimeString / format('Y-m-d H:i:s')) has no offset. Laravel runs
 *    on Europe/London (config/app.php), so those digits ARE London wall-clock. Feeding them to
 *    `new Date()` would reinterpret them as the viewer's zone, so we pin them to UTC and format
 *    as UTC — the digits then pass through untouched, which is what we want.
 *  - "18-Sep-2026" from the course billing rows, likewise already London and date-only.
 *  - anything carrying an offset (ISO-8601 with Z or +01:00) is a real instant, so we convert it
 *    into London.
 *
 * Doing it this way means a viewer in another timezone sees London time, without needing every
 * backend serializer changed in lockstep.
 */
const parseStamp = (value) => {
  const raw = String(value).trim();

  const naive = NAIVE_STAMP.exec(raw);
  if (naive) {
    const [, y, mo, d, h, mi, s] = naive;
    return {
      date: new Date(Date.UTC(+y, +mo - 1, +d, +h, +mi, +(s || 0))),
      timeZone: "UTC",
      dateOnly: false,
    };
  }

  const dmy = DMY_STAMP.exec(raw);
  if (dmy) {
    const month = MONTHS.indexOf(dmy[2].toLowerCase());
    if (month >= 0) {
      return {
        date: new Date(Date.UTC(+dmy[3], month, +dmy[1])),
        timeZone: "UTC",
        dateOnly: true,
      };
    }
  }

  const date = new Date(raw);
  return { date, timeZone: LONDON, dateOnly: false };
};

/** A sortable epoch for any of the shapes above; NaN-safe so a bad value sorts last. */
export const stampToMillis = (value) => {
  if (!value) return Number.NEGATIVE_INFINITY;
  const { date } = parseStamp(value);
  const ms = date.getTime();
  return Number.isNaN(ms) ? Number.NEGATIVE_INFINITY : ms;
};

export const formatUkDateTime = (value) => {
  if (!value) return "-";
  const { date, timeZone, dateOnly } = parseStamp(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    ...(dateOnly ? {} : { hour: "2-digit", minute: "2-digit", hour12: false }),
    timeZone,
  }).format(date);
};

const ordinalSuffix = (day) => {
  const mod100 = day % 100;
  if (mod100 >= 11 && mod100 <= 13) return "th";
  switch (day % 10) {
    case 1:
      return "st";
    case 2:
      return "nd";
    case 3:
      return "rd";
    default:
      return "th";
  }
};

export const formatPlanExpiryDate = (value) => {
  if (!value) return "-";
  const { date, timeZone } = parseStamp(value);
  if (Number.isNaN(date.getTime())) return String(value);
  // Read the day/month/year back in the same zone we format in, so a late-evening London
  // timestamp does not roll onto the next day for a viewer further east.
  const parts = new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone,
  }).formatToParts(date);
  const pick = (type) => parts.find((p) => p.type === type)?.value ?? "";
  const day = Number(pick("day"));
  return `${String(day).padStart(2, "0")}${ordinalSuffix(day)} ${pick("month")} ${pick("year").slice(-2)}`;
};

export const statusLabel = (status) => {
  const normalized = String(status || "").toLowerCase();
  if (normalized === "active") return "Active";
  if (normalized === "trial") return "Trial";
  if (normalized === "trial_ended_pending") return "Trial Ended - Pending";
  if (normalized === "cancelled") return "Cancelled";
  if (normalized === "expired") return "Expired";
  if (normalized === "completed" || normalized === "paid") return "Paid";
  if (normalized === "failed") return "Failed";
  // Stripe's own status, surfaced by the admin filters; it used to fall through and render
  // the raw "past_due" string at the user.
  if (normalized === "past_due") return "Payment overdue";
  if (normalized === "free") return "No plan";
  return status || "Unknown";
};

export const statusChipColor = (status) => {
  const normalized = String(status || "").toLowerCase();
  if (["active", "trial", "completed", "paid"].includes(normalized)) return "success";
  if (normalized === "trial_ended_pending") return "warning";
  if (normalized === "failed" || normalized === "past_due") return "error";
  return "default";
};

/**
 * `plan_state` from GET parent/mobile-subscriptions — the truthful state, unlike the raw
 * `subscription_status`, which stays on "active" forever when a renewal never lands.
 * Prefer this for anything a parent reads.
 */
export const planStateLabel = (planState, subscriptionStatus) => {
  switch (String(planState || "").toLowerCase()) {
    case "renewing":
      return statusLabel(subscriptionStatus);
    case "auto_renew_off":
      return "Auto-renew off";
    case "ended":
      return "Ended";
    // The card was declined but the paid period is still running — Stripe is retrying, so
    // this is a warning, not a lapse.
    case "payment_failed":
      return "Payment failed";
    case "none":
      return "No plan";
    default:
      return statusLabel(subscriptionStatus);
  }
};

export const planStateChipColor = (planState, subscriptionStatus) => {
  switch (String(planState || "").toLowerCase()) {
    case "renewing":
      return statusChipColor(subscriptionStatus);
    case "auto_renew_off":
      return "warning";
    case "ended":
      return "error";
    case "payment_failed":
      return "error";
    case "none":
      return "default";
    default:
      return statusChipColor(subscriptionStatus);
  }
};

/** How the date beside the chip should read for each state. */
export const planStateDateLabel = (planState) => {
  switch (String(planState || "").toLowerCase()) {
    case "auto_renew_off":
      return "access until";
    case "ended":
      return "ended";
    case "payment_failed":
      return "retrying until";
    case "renewing":
      return "renews";
    default:
      return null;
  }
};

/**
 * When a free trial ends, as "today at 3:19 pm", "tomorrow at 3:19 pm" or "on Fri 3 Oct at
 * 3:19 pm", in London time like every other plan date. A one-day trial needs the time, not
 * just the date: "ends 2 October" read as already over to someone who started it on the 1st.
 */
export const formatTrialEnd = (iso) => {
  if (!iso) return "";
  const end = new Date(iso);
  if (Number.isNaN(end.getTime())) return "";
  const londonDay = (d) => d.toLocaleDateString("en-CA", { timeZone: LONDON });
  const time = end.toLocaleTimeString("en-GB", { timeZone: LONDON, hour: "numeric", minute: "2-digit", hour12: true });
  const today = new Date();
  const tomorrow = new Date(today.getTime() + 86400000);
  if (londonDay(end) === londonDay(today)) return `today at ${time}`;
  if (londonDay(end) === londonDay(tomorrow)) return `tomorrow at ${time}`;
  const date = end.toLocaleDateString("en-GB", { timeZone: LONDON, weekday: "short", day: "numeric", month: "short" });
  return `on ${date} at ${time}`;
};
